import { Fragment, useState } from "react";
import refresh from "@/assets/refresh.svg";
import { IoLocationSharp } from "react-icons/io5";
import { BsCurrencyRupee } from "react-icons/bs";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import CheckPrices from "@/app/components/routes/auth_routes/Appointments/CheckPrices";
import NearestclinicComponent from "@/app/components/routes/auth_routes/SlotsScreen/NearestclinicComponent";
import SlotsUserDetailsHeaderComponent from "@/app/components/routes/auth_routes/SlotsScreen/SlotsUserDetails";
import { IBookingFiltersHeaderProps } from "@/app/components/routes/auth_routes/SlotsScreen/BookingFiltersComponent/interface";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";

const BookingFiltersHeader: React.FC<IBookingFiltersHeaderProps> = ({
  isPageReadonly,
  shouldShowLatestBookingChip,
  applyLastBookingFilters,
  disableSlotsFilter,
  isAtHomeReschedule,
  isLatestBookingInProgress,
  latestBooking,
  latestBookingTitle,
  latestBookingClinicAndCity,
  handleCheckPriceClick,
  handelSelectClinic,
  isCheckPricesOpen,
  setIsCheckPricesOpen,
}) => {
  const [nearestClinic, setNearestClinic] = useState(false);
  const { getRouteKey } = useRouter();
  const { rescheduleFlag } = useRouteInfo(getRouteKey("SLOTS", "id")).routeState.state as ISlotsState;
  const isLastBookingChipDisabled = disableSlotsFilter || isAtHomeReschedule || isLatestBookingInProgress || !latestBooking;
  return (
    <>
      <div
        className={clsx(
          "w-full bg-white p-3 my-2 rounded-xl",
          rescheduleFlag ? "flex flex-col gap-2" : "flex justify-between items-center",
        )}
      >
        {isPageReadonly ? (
          <p className="font-semibold text-base">Check Available slot</p>
        ) : (
          <SlotsUserDetailsHeaderComponent />
        )}
        <div className={clsx("flex items-center justify-end flex-nowrap", rescheduleFlag && "w-full")}>
          <div className="h-[40px] w-full max-w-[690px] rounded-[10px] bg-[#F3F2F2] flex items-center p-[2px]">
            {shouldShowLatestBookingChip && (
              <button
                type="button"
                onClick={applyLastBookingFilters}
                disabled={isLastBookingChipDisabled}
                className={`h-full flex-1 px-3 rounded-[8px] flex items-center gap-2 transition min-w-0 ${isLastBookingChipDisabled
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer hover:bg-[#ECECEC]"
                  }`}
              >
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <img src={refresh} alt="Refresh" />
                </div>
                <div className="text-left min-w-0">
                  <p
                    className="truncate text-[12px] font-medium leading-[100%] text-[#0E64DD]"
                    style={{
                      fontWeight: 500,
                      fontStyle: "normal",
                      letterSpacing: "0px",
                      verticalAlign: "middle",
                      textDecorationLine: "underline",
                      textDecorationStyle: "wavy",
                      textUnderlineOffset: "11%",
                      textDecorationThickness: "11%",
                      textDecorationSkipInk: "auto",
                    }}
                  >
                    <span>Last Booking :</span>
                    <span className="ml-1">
                      {isLatestBookingInProgress && !latestBooking ? "Fetching latest booking..." : latestBookingTitle}
                    </span>
                  </p>
                  <p
                    className="truncate mt-[2px] text-[12px] font-medium leading-[100%] text-[#6B6B6B]"
                    style={{
                      fontWeight: 500,
                      fontStyle: "normal",
                      letterSpacing: "0px",
                      verticalAlign: "middle",
                    }}
                  >
                    {latestBooking
                      ? latestBookingClinicAndCity || "Tap to auto-fill filters"
                      : "No previous booking found"}
                  </p>
                </div>
              </button>
            )}
            <button
              type="button"
              className="whitespace-nowrap flex items-center gap-2 h-full px-4 rounded-[8px] border border-[#F05B72] bg-[#FFF4F4] text-[#F05B72] font-medium text-xs transition shrink-0"
              onClick={() => setNearestClinic(true)}
            >
              <IoLocationSharp className="w-4 h-4" />
              Find Nearest clinic
            </button>
            <button
              type="button"
              className="whitespace-nowrap flex items-center gap-2 h-full px-4 ml-2 rounded-[8px] border border-[#0E64DD] bg-[#EAF2FD] text-[#0E64DD] font-medium text-xs transition shrink-0"
              onClick={handleCheckPriceClick}
            >
              <BsCurrencyRupee className="w-4 h-4" />
              Check Price
            </button>
          </div>
        </div>
      </div>
      {nearestClinic && (
        <NearestclinicComponent
          open={nearestClinic}
          setOpen={setNearestClinic}
          handelSelectClinic={handelSelectClinic}
        />
      )}
      <Transition.Root show={isCheckPricesOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsCheckPricesOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-5xl h-[60vh] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all flex flex-col">
                  <button
                    type="button"
                    className="absolute right-3 top-3 z-10 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => setIsCheckPricesOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div className="flex-1 overflow-y-auto">
                    <CheckPrices />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default BookingFiltersHeader;
